import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, Link2 } from 'lucide-react';

const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join('; ') || fb;
  if (d && typeof d === 'object') return d.msg || fb;
  return e?.message || fb;
};

/**
 * Vincula um cliente já cadastrado a um painel conectado, sem apagar/recriar.
 * Não altera nada além do vínculo (usuário/pacote) e do vencimento.
 */
export default function LinkCustomerDialog({ open, onOpenChange, api, customer, onLinked }) {
  const [sources, setSources] = useState([]);
  const [integId, setIntegId] = useState('');
  const [plans, setPlans] = useState([]);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [provider, setProvider] = useState('sigma');
  const [chosenPid, setChosenPid] = useState('');
  const [planId, setPlanId] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResults(null); setChosenPid(''); setPlanId(''); setQuery('');
    api.get('/imports/sources').then((r) => {
      const list = r.data.sources || [];
      setSources(list);
      setIntegId((prev) => prev || (list[0]?.id || ''));
    }).catch(() => {});
    api.get('/customer-plans').then((r) => setPlans(r.data || [])).catch(() => {});
    setQuery(customer?.sigma_username || customer?.name || '');
  }, [open, api, customer]);

  const source = sources.find((s) => s.id === integId);
  const isPanelFoda = source?.provider === 'painelfoda';
  const plansForSource = plans.filter((p) => p.active !== false && p.sigma_login_integration_id === integId);

  const search = async () => {
    if (!integId) { toast.error('Escolha o painel'); return; }
    try {
      setSearching(true); setResults(null); setChosenPid('');
      const r = await api.post(`/customers/${customer.id}/link-search`, { integration_id: integId, query: query.trim() || null });
      setProvider(r.data.provider);
      setResults(r.data.customers || []);
      const first = (r.data.customers || []).find((c) => !c.already_linked);
      if (first) setChosenPid(first.panel_customer_id);
      if (!(r.data.customers || []).length) toast.info('Nenhum cliente encontrado no painel com esse usuário');
    } catch (e) {
      toast.error(errText(e, 'Erro ao buscar no painel'));
    } finally {
      setSearching(false);
    }
  };

  const apply = async () => {
    if (!chosenPid) { toast.error('Escolha o cliente correspondente no painel'); return; }
    if (isPanelFoda && !planId) { toast.error('Escolha o plano do painel para este cliente'); return; }
    try {
      setLinking(true);
      const r = await api.post(`/customers/${customer.id}/link-apply`, {
        integration_id: integId, panel_customer_id: chosenPid, plan_id: isPanelFoda ? planId : null,
      });
      toast.success(r.data.message || 'Cliente vinculado');
      onOpenChange(false);
      onLinked && onLinked();
    } catch (e) {
      toast.error(errText(e, 'Erro ao vincular'));
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vincular {customer?.name} a um painel</DialogTitle>
          <DialogDescription>
            Liga este cliente ao painel sem apagar nada. Só o vínculo e o vencimento mudam — nome, preço e o resto ficam como estão.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Painel</Label>
            <Select value={integId} onValueChange={(v) => { setIntegId(v); setResults(null); setPlanId(''); }}>
              <SelectTrigger><SelectValue placeholder="Escolha o painel" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Input placeholder="Usuário do cliente no painel" value={query}
              onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} />
            <Button onClick={search} disabled={searching || !integId}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {searching && <p className="text-xs text-muted-foreground">Consultando o painel… pode levar alguns segundos.</p>}

          {results && results.length > 0 && (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {results.map((c) => (
                <label key={c.panel_customer_id}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${chosenPid === c.panel_customer_id ? 'border-primary bg-primary/5' : 'border-border/60'} ${c.already_linked ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <input type="radio" name="pid" disabled={c.already_linked} checked={chosenPid === c.panel_customer_id}
                      onChange={() => setChosenPid(c.panel_customer_id)} />
                    <div className="min-w-0">
                      <p className="font-mono text-sm truncate">{c.username}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.package_name || '—'} · vence {(c.expiration_date || '').split('T')[0] || '—'}
                      </p>
                    </div>
                  </div>
                  {c.already_linked && <Badge variant="outline" className="text-[10px] shrink-0">Já vinculado</Badge>}
                </label>
              ))}
            </div>
          )}

          {isPanelFoda && chosenPid && (
            <div className="space-y-1.5 rounded-lg border border-border/60 p-3 bg-muted/20">
              <Label className="text-xs">Plano a aplicar (pacote do painel)</Label>
              {plansForSource.length > 0 ? (
                <Select value={planId} onValueChange={setPlanId}>
                  <SelectTrigger><SelectValue placeholder="Escolha o plano deste cliente" /></SelectTrigger>
                  <SelectContent>
                    {plansForSource.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — R$ {Number(p.price || 0).toFixed(2)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-[11px] text-amber-500">Nenhum plano deste painel ainda. Crie um em Planos → Novo plano (vinculado a este painel) antes de vincular.</p>
              )}
            </div>
          )}

          {results && (
            <Button className="w-full" onClick={apply} disabled={linking || !chosenPid || (isPanelFoda && !planId)}>
              {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Vincular ao painel
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
